/**
 * Payment utility functions for Stripe integration
 */

import { API_URL } from '../config';

/**
 * Initiate Stripe checkout for a service
 * @param {string} serviceType - 'copyright', 'publishing', 'screenplay', 'tv_pilot'
 * @param {object} metadata - Additional metadata to store with payment
 * @returns {Promise<{url: string, session_id: string}>}
 */
export async function initiateServicePayment(serviceType, metadata = {}) {
  const token = localStorage.getItem('token');
  const originUrl = window.location.origin;

  const response = await fetch(`${API_URL}/api/payments/services/create-checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      service_type: serviceType,
      origin_url: originUrl,
      metadata
    })
  });

  // Read response body once
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || 'Failed to create payment session');
  }

  return data;
}

/**
 * Check if user has already paid for a service
 * @param {string} serviceType - Service type to check
 * @returns {Promise<{paid: boolean, transaction_id?: string, amount?: number}>}
 */
export async function checkServicePayment(serviceType) {
  const token = localStorage.getItem('token');

  const response = await fetch(`${API_URL}/api/payments/services/verify/${serviceType}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  // Read response body once
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || 'Failed to verify payment status');
  }

  return data;
}

/**
 * Poll payment status after Stripe redirect
 * @param {string} sessionId - Stripe checkout session ID
 * @param {number} maxAttempts - Maximum number of polling attempts
 * @returns {Promise<object>} Payment status
 */
export async function pollPaymentStatus(sessionId, maxAttempts = 10) {
  const token = localStorage.getItem('token');
  const pollInterval = 2000; // 2 seconds

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const response = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    // Read response body once
    const status = await response.json();

    if (!response.ok) {
      throw new Error(status.detail || 'Failed to check payment status');
    }

    // If payment is complete, return immediately
    if (status.payment_status === 'paid') {
      return { success: true, ...status };
    }

    // If session expired, stop polling
    if (status.status === 'expired') {
      return { success: false, expired: true, ...status };
    }

    // Wait before next attempt
    if (attempt < maxAttempts - 1) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
  }

  // Timed out
  return { success: false, timeout: true };
}

/**
 * Get service details for display
 */
export const SERVICE_DETAILS = {
  copyright: {
    name: 'Copyright Registration',
    price: 99,
    description: 'Professional copyright registration with WGA & U.S. Copyright Office'
  },
  publishing: {
    name: 'Amazon Publishing Service',
    price: 299,
    description: 'Complete Amazon KDP setup and optimization'
  },
  screenplay: {
    name: 'Screenplay Conversion',
    price: 149,
    description: 'Convert your novel to Hollywood-ready screenplay format'
  },
  tv_pilot: {
    name: 'TV Pilot Conversion',
    price: 149,
    description: 'Convert your story to a professional TV pilot script'
  }
};
